import { useGetUserAllowedOrganizationTags } from '@/api/trafikportalen';
import { useCallback, useEffect, useMemo } from 'react';
import { SelectableList } from '../SelectableList';
import { collator } from '@/utils/collator';
import { GroupableTicket, groupTicketsByProduct } from '@/helpers/groupTickets';
import type { BaseTicket } from '@/types/buyTicket';
import { useGetCorporateStaticOffers } from '@/hooks/useGetCorporateStaticOffers';

interface StaticTicketsProps {
  selectedBaseTicketId: string | undefined;
  onSelectBaseTicket: (baseTicket: BaseTicket | undefined) => void;
}

export const StaticTickets = ({
  selectedBaseTicketId,
  onSelectBaseTicket,
}: StaticTicketsProps) => {
  const { allowedOffers, isLoading } = useGetCorporateStaticOffers();
  const { data: tags } = useGetUserAllowedOrganizationTags();

  const baseTickets = useMemo(() => {
    if (!allowedOffers || !tags) {
      return [];
    }

    const normalized = allowedOffers.map((offer): GroupableTicket => {
      const pcTag = offer.tags.find((tag) => tag.startsWith('pc_'));
      const productClassTag = tags.find((tag) => tag.tag_id === pcTag);
      const title = productClassTag
        ? `${offer.title} (${productClassTag.title})`
        : offer.title;

      return {
        product_set_id: offer.product_set_id,
        title,
        currency: offer.currency,
        amount: offer.amount_inc_vat,
        tags: offer.tags,
      };
    });

    return groupTicketsByProduct(normalized).sort((a, b) =>
      collator.compare(a.title, b.title),
    );
  }, [allowedOffers, tags]);

  const options = useMemo(
    () =>
      baseTickets.map((baseTicket) => ({
        id: baseTicket.product_set_id,
        label: baseTicket.title,
      })),
    [baseTickets],
  );

  const handleSelect = useCallback(
    (productSetId: string) => {
      onSelectBaseTicket(
        baseTickets.find((ticket) => ticket.product_set_id === productSetId),
      );
    },
    [baseTickets, onSelectBaseTicket],
  );

  useEffect(() => {
    if (baseTickets.length === 0) {
      if (selectedBaseTicketId) {
        onSelectBaseTicket(undefined);
      }
      return;
    }

    const isStillValid = baseTickets.some(
      (ticket) => ticket.product_set_id === selectedBaseTicketId,
    );
    if (!isStillValid) {
      onSelectBaseTicket(baseTickets[0]);
    }
  }, [baseTickets, selectedBaseTicketId, onSelectBaseTicket]);

  return (
    <SelectableList
      options={options}
      selectedOption={selectedBaseTicketId || ''}
      setSelectedOption={handleSelect}
      className="h-96"
      isLoading={isLoading}
    />
  );
};
